import { gql } from "@apollo/client";
import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";

import type {
  MenuSectionEditResp,
  MenuSectionEditVariables,
  MenuSectionVariables,
  MenuSection
} from "../../types/menuSectionTypes";

const UPDATE_MENU_SECTION = gql`
  mutation UpdateMenuSection($attributes: UpdateMenuSectionInput!) {
    updateMenuSection(attributes: $attributes) {
      menuSection {
        id
        name
        position
        menuId
      }
      errors
    }
  }
`   

type UseUpdateMenuSectionOptions = {
  onCompleted?: (menuSection: MenuSection) => void;
  onError?: (error: unknown) => void;
  mutationOptions?: Parameters<typeof useMutation<MenuSectionEditResp, MenuSectionEditVariables>>[1];
};

const useUpdateMenuSection = (options?: UseUpdateMenuSectionOptions) => {
  const [mutate, { data, loading, error }] = useMutation<
    MenuSectionEditResp,
    MenuSectionEditVariables
  >(UPDATE_MENU_SECTION, options?.mutationOptions);

  const updateMenuSection = useCallback(
    async (input: MenuSectionVariables & { id: number }) => {
      try {
        const result = await mutate({
          variables: {
            attributes: {
              id: input.id!,
              position: input.position,
              menuId: input.menuId,
              name: input.name 
            },
          },
        });

        const updateMenuSection = result?.data?.updateMenuSection;

        if (updateMenuSection?.errors?.length) {
          throw new Error(updateMenuSection.errors.join(", "));
        }

        if (options?.onCompleted && updateMenuSection?.menuSection) {
          options.onCompleted(updateMenuSection.menuSection);
        }

        return updateMenuSection?.menuSection;
      } catch (err) {
        if (options?.onError) {
          options.onError(err);
        }
        throw err;
      }
    },
    [mutate, options]
  );

  const menuSection = data?.updateMenuSection?.menuSection;

  return {
    updateMenuSection,
    updatedMenuSection: menuSection,
    loading,
    error: error as Error | null | undefined,
  };
}

export default useUpdateMenuSection
